/**
 * Strategy Simulation.
 *
 * A deliberate, on-demand ACTION ("Run Simulation") rather than another
 * auto-recalculating card like Risk & Greeks above it — the trader edits
 * the Trade Builder, waits for Risk & Greeks to settle, then explicitly
 * clicks Run Simulation when ready to see the payoff table. This avoids
 * firing a simulation call on every keystroke for what is a secondary,
 * point-in-time analysis rather than a live-updating figure.
 *
 * Reuses the existing Risk & Greeks engine end to end: the simulation call
 * (`strategyAdjustmentService.simulate`) hits a backend endpoint that
 * samples the SAME payoff curve `recalculate()` already uses to derive Max
 * Risk/Max Profit/Break-even (`metrics_calculator._price_leg`/
 * `_payoff_at_price`), just at a small set of trader-facing price
 * scenarios. Nothing here re-implements pricing, and nothing here is a
 * broker order, a paper-trade fill, or a virtual position — it is a
 * read-only "what if the underlying moved" table for the strategy exactly
 * as currently built (same underlying, expiry, legs, strikes, side,
 * quantity the Trade Builder above is showing).
 *
 * Always uses the CURRENT `legs`/`underlyingPriceOverride` props at the
 * moment "Run Simulation" is clicked — if the trader edits the strategy
 * after running a simulation, the existing result is kept on screen (nothing
 * disappears out from under them) but flagged stale via `staleSince` so
 * they know to re-run rather than act on outdated numbers.
 *
 * The Payoff Visualizer used to be rendered here, fetched on-demand
 * alongside `simulate()` — it is now its own always-live card directly on
 * `StrategyAdjustmentPage` (interactive drag-to-adjust-strikes, single
 * source of truth with the Trade Builder's `legs`; see `PayoffChart.tsx`'s
 * own docstring), so this card no longer fetches or renders it — fetching
 * the SAME curve twice (once live above, once again here on click) would
 * be redundant and could show two charts with different recency on one
 * page. This card's own remaining job (an on-demand scenario table at a
 * small set of price moves) is unaffected.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import strategyAdjustmentService from "@/services/strategyAdjustmentService";
import type { OptionLeg, SimulationResultRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { fmtCurrency, fmtNum } from "./constants";

function legsSignature(legs: OptionLeg[]): string {
  return JSON.stringify(
    legs.map((leg) => [leg.option_type, leg.side, leg.strike, leg.expiry_date, leg.quantity, leg.lot_size])
  );
}

function moveLabel(pricMovePct: number): string {
  if (pricMovePct === 0) return "Current";
  return pricMovePct > 0 ? `+${pricMovePct}%` : `${pricMovePct}%`;
}

export function SimulationCard({
  symbol,
  legs,
  underlyingPriceOverride,
  onProceedToExecution,
}: {
  symbol: string;
  legs: OptionLeg[];
  underlyingPriceOverride: number | null;
  onProceedToExecution?: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SimulationResultRead | null>(null);
  const [simulatedSignature, setSimulatedSignature] = useState<string | null>(null);

  const isStale = result !== null && simulatedSignature !== legsSignature(legs);

  const handleRun = async () => {
    if (!symbol || legs.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const currentSignature = legsSignature(legs);
      const simulation = await strategyAdjustmentService.simulate(symbol, legs, underlyingPriceOverride);
      setResult(simulation);
      setSimulatedSignature(currentSignature);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToBuilder = () => {
    setResult(null);
    setSimulatedSignature(null);
  };

  const failed = result?.calculation_status === "FAILED";

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
            Simulate Strategy
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <PlayCircleOutlineIcon fontSize="small" />}
            onClick={handleRun}
            disabled={legs.length === 0 || loading}
          >
            {loading ? "Simulating..." : result ? "Re-run Simulation" : "Run Simulation"}
          </Button>
        </Stack>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!result && !loading && (
          <Typography variant="body2" color="text.secondary">
            See how this strategy's P&amp;L would look at expiry across a range of underlying-price moves, using the
            same legs and risk model as Risk &amp; Greeks above.
          </Typography>
        )}

        {result && (
          <Box>
            {isStale && (
              <Alert severity="info" sx={{ mb: 2 }}>
                The strategy has changed since this simulation ran — re-run to see the current legs.
              </Alert>
            )}

            {failed ? (
              <Alert severity="error">
                <Typography variant="body2" fontWeight={700}>
                  Unable to calculate
                </Typography>
                <Typography variant="body2">{result.calculation_error ?? "Unknown calculation error."}</Typography>
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Underlying price used: <strong>{fmtNum(result.underlying_price, 2)}</strong> · Net premium:{" "}
                  <strong>{fmtCurrency(result.net_premium)}</strong>
                </Typography>

                {!result.is_single_expiry ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This strategy spans more than one expiry, so an exact at-expiry payoff can't be computed the same
                    way — the far-month leg still has time value left. Only net premium is shown; see Risk &amp;
                    Greeks above for what is quantifiable for this combination.
                  </Alert>
                ) : (
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Move</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">
                            Underlying Price
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align="right">
                            P&amp;L at Expiry
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.scenarios.map((scenario) => (
                          <TableRow key={scenario.price_move_pct} hover selected={scenario.price_move_pct === 0}>
                            <TableCell>{moveLabel(scenario.price_move_pct)}</TableCell>
                            <TableCell align="right">{fmtNum(scenario.underlying_price, 2)}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 600,
                                color:
                                  scenario.payoff == null
                                    ? "text.secondary"
                                    : scenario.payoff >= 0
                                      ? "success.main"
                                      : "error.main",
                              }}
                            >
                              {fmtCurrency(scenario.payoff)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={handleReturnToBuilder}>
                Back to Strategy Builder
              </Button>
              {onProceedToExecution && (
                <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon fontSize="small" />} onClick={onProceedToExecution}>
                  Proceed to Execution
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default SimulationCard;
